'use client';

import { c as _c } from "react/compiler-runtime";
import { useEffect, useState, useCallback, useSyncExternalStore } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Cookie } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useCookieConsent } from '@/entities/cookie-consent';
import { requiresCookieConsent } from '@/lib/feature-flags';
import { cn } from '@/lib/utils';
import { CookiePreferencesModal } from './cookie-preferences-modal';
const subscribeNever = () => () => {};
const isOnClient = () => true;
const isOnServer = () => false;
interface CookieConsentBannerProps {
  className?: string;
}

/**
 * GDPR-compliant cookie consent banner
 *
 * Features:
 * - Fixed position at bottom of screen
 * - Equal prominence for Accept/Reject buttons (GDPR requirement)
 * - "Manage Preferences" link opens granular selection modal
 * - Only shown when NEXT_PUBLIC_REQUIRES_COOKIE_CONSENT is 'true'
 * - Auto-hides after consent decision
 * - Smooth slide-up animation
 */
export function CookieConsentBanner(t0) {
  const $ = _c(24);
  const {
    className
  } = t0;
  const {
    hasConsented,
    acceptAll,
    rejectAll
  } = useCookieConsent();
  const [isVisible, setIsVisible] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const isHydrated = useSyncExternalStore(subscribeNever, isOnClient, isOnServer);
  let t1;
  let t2;
  if ($[0] !== hasConsented || $[1] !== isHydrated) {
    t1 = () => {
      if (isHydrated && requiresCookieConsent() && !hasConsented) {
        const timer = setTimeout(() => setIsVisible(true), 500);
        return () => clearTimeout(timer);
      }
    };
    t2 = [isHydrated, hasConsented];
    $[0] = hasConsented;
    $[1] = isHydrated;
    $[2] = t1;
    $[3] = t2;
  } else {
    t1 = $[2];
    t2 = $[3];
  }
  useEffect(t1, t2);
  let t3;
  if ($[4] !== acceptAll) {
    t3 = () => {
      acceptAll();
      setIsVisible(false);
    };
    $[4] = acceptAll;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const handleAccept = t3;
  let t4;
  if ($[6] !== rejectAll) {
    t4 = () => {
      rejectAll();
      setIsVisible(false);
    };
    $[6] = rejectAll;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  const handleReject = t4;
  let t5;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = () => {
      setIsVisible(false);
      setIsModalOpen(true);
    };
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  const handleManagePreferences = t5;
  let t6;
  if ($[9] !== hasConsented) {
    t6 = open => {
      setIsModalOpen(open);
      if (!open && !hasConsented) {
        setIsVisible(true);
      }
    };
    $[9] = hasConsented;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  const handleModalClose = t6;
  const handleModalSave = _temp;
  if (!isHydrated) {
    return null;
  }
  if (!requiresCookieConsent() || hasConsented) {
    return null;
  }
  let t7;
  if ($[11] !== className || $[12] !== handleAccept || $[13] !== handleReject || $[14] !== isVisible) {
    t7 = isVisible && <motion.div initial={{
      y: 100,
      opacity: 0
    }} animate={{
      y: 0,
      opacity: 1
    }} exit={{
      y: 100,
      opacity: 0
    }} transition={{
      type: "spring",
      stiffness: 300,
      damping: 30
    }} className={cn("fixed bottom-0 left-0 right-0 z-100 p-4", className)} role="dialog" aria-label="Cookie consent" aria-describedby="cookie-consent-description" data-testid="cookie-banner"><div className="mx-auto max-w-4xl"><div className="bg-white rounded-xl shadow-lg border border-gray-200 p-4 md:p-6"><div className="flex flex-col md:flex-row md:items-center gap-4"><div className="flex items-start gap-3 flex-1"><div className="shrink-0 w-10 h-10 rounded-full bg-[#6252fd]/10 flex items-center justify-center"><Cookie className="h-5 w-5 text-[#6252fd]" /></div><div className="flex-1 min-w-0"><h3 className="text-sm font-semibold text-gray-900">We value your privacy</h3><p id="cookie-consent-description" className="text-sm text-gray-600 mt-1">We use cookies to enhance your experience and analyse site usage. You can accept or reject non-essential cookies.{" "}<a href="https://www.cova.ai/privacy" className="text-[#6252fd] hover:underline" target="_blank" rel="noopener noreferrer">Learn more</a></p><button onClick={handleManagePreferences} className="text-sm text-[#6252fd] hover:underline mt-1 font-medium" type="button" data-testid="cookie-manage-prefs">Manage preferences</button></div></div><div className="flex items-center gap-3 shrink-0"><Button variant="outline" onClick={handleReject} className="flex-1 md:flex-none min-w-[100px] border-gray-300 text-gray-700 hover:bg-gray-50" data-testid="cookie-reject-all">Reject</Button><Button onClick={handleAccept} className="flex-1 md:flex-none min-w-[100px] bg-[#6252fd] hover:bg-[#5242ed] text-white" data-testid="cookie-accept-all">Accept</Button></div></div></div></div></motion.div>;
    $[11] = className;
    $[12] = handleAccept;
    $[13] = handleReject;
    $[14] = isVisible;
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  let t8;
  if ($[16] !== t7) {
    t8 = <AnimatePresence>{t7}</AnimatePresence>;
    $[16] = t7;
    $[17] = t8;
  } else {
    t8 = $[17];
  }
  let t9;
  if ($[18] !== handleModalClose || $[19] !== isModalOpen) {
    t9 = <CookiePreferencesModal open={isModalOpen} onOpenChange={handleModalClose} onSave={handleModalSave} />;
    $[18] = handleModalClose;
    $[19] = isModalOpen;
    $[20] = t9;
  } else {
    t9 = $[20];
  }
  let t10;
  if ($[21] !== t8 || $[22] !== t9) {
    t10 = <>{t8}{t9}</>;
    $[21] = t8;
    $[22] = t9;
    $[23] = t10;
  } else {
    t10 = $[23];
  }
  return t10;
}
function _temp() {}